import { EProductPlanPaymentInterval } from "@devowl-wp/api-real-commerce";
const RECURRING_COUPON_TOLERANCE = 0.01;
const INTRO_PRICING_TOLERANCE = 0.005;
/**
 * Pure derivation of every per-row value of a Checkout v2 order summary
 * from a complete snapshot of the buyer's pricing context: list prices +
 * payment interval, Paddle's per-buyer prices and tax (after the buyer's
 * country / VAT context is known), pre-coupon baselines, a coupon-applied
 * flag, and an optional cashback promotion. Single source of truth for
 * any consumer that needs to render the same breakdown — checkout UI,
 * cart-abandonment email, purchase-success email, etc.
 *
 * No side effects, no localisation, no string formatting — every locale-,
 * DOM-, or React-bound transformation lives in the consumer.
 */ function computeOrderSummary(input) {
    const { currency, paymentInterval, hasActiveIdentification, initialPrice, initialPriceTax, recurringPrice, recurringPriceTax, preCouponInitialPrice, preCouponRecurringPrice, hasCouponApplied, cashbackPromotion } = input;
    // The API ships `decimal` columns as strings. Coerce defensively even
    // though the input contract types them as `number` — a missing coercion
    // higher up would otherwise silently break the recurring-coupon tolerance
    // check below (string subtraction yields `NaN`).
    const originalInitialPrice = Number(input.originalInitialPrice);
    const originalRecurringPrice = Number(input.originalRecurringPrice);
    const isOneTime = paymentInterval === EProductPlanPaymentInterval.OneTime;
    // Mirror the per-buyer pre-coupon baselines Paddle reports on every
    // event. They already factor in any reverse-charge VAT removal, so the
    // displayed savings stay free of conflated VAT deltas.
    const perBuyerBaselineInitial = typeof preCouponInitialPrice === "number" ? preCouponInitialPrice : originalInitialPrice;
    const perBuyerBaselineRecurring = typeof preCouponRecurringPrice === "number" ? preCouponRecurringPrice : originalInitialPrice;
    // Equivalent of `Transaction#currentInitialPrice` / `currentRecurringPrice`,
    // gated by `hasActiveIdentification` so an Edit-Details flow drops back to
    // list-price visuals even while the transaction still holds the last
    // applied Paddle values.
    const currentInitialPrice = hasActiveIdentification && typeof initialPrice === "number" ? initialPrice : originalInitialPrice;
    const currentRecurringPrice = hasActiveIdentification && typeof recurringPrice === "number" ? recurringPrice : undefined;
    const grossPrice = currentInitialPrice;
    const taxAmount = hasActiveIdentification ? initialPriceTax : undefined;
    const hasTaxAmount = typeof taxAmount === "number";
    const taxRatePercent = hasTaxAmount && taxAmount > 0 && grossPrice > taxAmount ? Math.round(taxAmount / (grossPrice - taxAmount) * 100) : 0;
    const hasCoupon = hasActiveIdentification && hasCouponApplied;
    // A recurring coupon yields equal first-term and recurring per-buyer
    // prices; a one-time coupon only touches the first term. Comparing per-
    // buyer prices (not list prices) avoids falsely flagging a B2B reverse-
    // charge buyer (whose recurring drops below list because of VAT removal).
    const isRecurringCoupon = hasCoupon && !isOneTime && typeof currentRecurringPrice === "number" && Math.abs(grossPrice - currentRecurringPrice) < RECURRING_COUPON_TOLERANCE;
    // Explicit discount line only fires for one-time coupons. Recurring
    // coupons surface savings via the strike-through, so the row would
    // double-count.
    const discountAmount = hasCoupon && !isRecurringCoupon ? Math.max(0, perBuyerBaselineInitial - grossPrice) : 0;
    // Intro pricing: list-level recurring differs from list-level first-term
    // (e.g. "first year 250 €, afterwards 499 €/year"). Detected on list
    // prices only — a one-time coupon also widens that gap, but the discount
    // row already explains that delta.
    const hasIntroPricing = Number.isFinite(originalRecurringPrice) && Math.abs(originalRecurringPrice - originalInitialPrice) > INTRO_PRICING_TOLERANCE && !isOneTime;
    // The bold product-row figure: when a one-time coupon is active we show
    // the per-buyer pre-coupon baseline so the adjacent discount row reads as
    // a real reduction. Without a coupon we keep Paddle's per-buyer gross.
    const productPriceBase = hasCoupon ? perBuyerBaselineInitial : grossPrice;
    // "Total today" only when today's charge actually differs from later
    // ones. OTP carts have no later charge; subscription + recurring coupon
    // keeps the same amount on every renewal, so both render plain "Total".
    const totalIsToday = !isOneTime && (discountAmount > 0 || hasIntroPricing);
    let taxState;
    if (hasTaxAmount && taxAmount > 0) {
        taxState = {
            kind: "vat-charged",
            ratePercent: taxRatePercent,
            amount: taxAmount
        };
    } else if (hasTaxAmount && taxAmount === 0) {
        taxState = {
            kind: "reverse-charge"
        };
    } else {
        taxState = {
            kind: "pending"
        };
    }
    // Renewal disclosure mirrors `Transaction#labelRecurringPrice`'s gating:
    // only when intro pricing is active *and* Paddle has reported a recurring
    // figure. The VAT parenthetical inside the consumer-side label is dropped
    // via `recurringVatAmount=undefined` when reverse charge or no Paddle
    // data is in scope, so consumers don't render "(incl. EUR 0.00 VAT)".
    let recurringDisclosure;
    if (hasIntroPricing && typeof currentRecurringPrice === "number") {
        const hasRecurringVat = typeof recurringPriceTax === "number" && recurringPriceTax > 0;
        recurringDisclosure = {
            recurringAmount: currentRecurringPrice,
            recurringVatAmount: hasRecurringVat ? recurringPriceTax : undefined
        };
    }
    // The product row's three render modes are mutually exclusive; recurring
    // coupon takes precedence over intro pricing when both apply (intro
    // pricing still surfaces via the renewal disclosure block above).
    let productRow;
    if (isRecurringCoupon && typeof currentRecurringPrice === "number") {
        productRow = {
            kind: "recurringCoupon",
            couponedRecurringPrice: currentRecurringPrice,
            perBuyerRecurringPrice: perBuyerBaselineRecurring
        };
    } else if (hasIntroPricing) {
        productRow = {
            kind: "introPricing",
            price: productPriceBase,
            listRecurringPrice: originalRecurringPrice
        };
    } else {
        productRow = {
            kind: "plain",
            price: productPriceBase
        };
    }
    return {
        currency,
        paymentInterval,
        productRow,
        recurringDisclosure,
        couponDiscountAmount: discountAmount > 0 ? discountAmount : undefined,
        cashbackPromotionName: cashbackPromotion?.name,
        taxState,
        totalAmount: grossPrice,
        totalIsPaidToday: totalIsToday
    };
}
export { computeOrderSummary };
